import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { StyleSheet, Text, View } from "react-native";
import { randomUUID } from "expo-crypto";
import type { AuthUser } from "../../../runtime/ports";
import { useData, useRuntime } from "../../../runtime/provider";
import { track } from "@utils/rudderstack";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";
// Amounts are ISO 4217 minor units of their currency (money.ts).
import { storeMoneyMinor as money, withLocalTax } from "@libs/platform-wire";
import { orderTotal, type Purchase } from "@libs/platform-wire/purchase";
import { goodsWords } from "@libs/buy-on-behalf-client/goods";
import {
  ORDER_CHANGED,
  createPurchaseDelivery,
  demoChangeable,
  optionLabelOfPicker,
  optionSetOfPicker,
  type FormDraft,
  type PurchaseDeliveryController,
  type PurchaseDeliveryQuote,
  type PurchaseTransport,
} from "@libs/buy-on-behalf-client/purchase";
import { settledQuote } from "@libs/buy-on-behalf-client/delivery";
import {
  heldLines,
  lineQuantities,
  openingLines,
  type OptionLine,
} from "@libs/buy-on-behalf-client/options";
import { useSheetFunnelOpening } from "@libs/shared-ui";
import type { EventTier, SheetFunnelOpening } from "@libs/shared-ui";
import LPressable from "@components/LPressable";
import { readAccessPurchase } from "@hooks/trpc/useAccesses";
import { useAppBuyer } from "../appBuyer";
import { Chevron } from "../native/Chevron";
import { colors } from "../native/colors";
import { OrderSummaryView } from "../native/OrderSummaryCard";
import { PurchaseSheet, purchaseSelection } from "../PurchaseSheet";
import { usePurchaseFunnel } from "../purchaseFunnel";
import type { CheckoutHost, PurchaseFlow } from "../PurchaseSheet";
import { TicketSelection, TicketsSheet } from "../TicketsSheet";
import { attemptKey, useAttemptWriter, useRestoredAttempt } from "./attemptStore";
import { DeliveryChoiceView, DeliveryQuoteNote } from "./MerchDelivery";
import { VendorCheckout } from "./VendorCheckout";
import { purchaseOrderOf, type OrderDelivery } from "./vendorCheckoutShared";
import { VendorShape } from "./VendorShape";

type Item = Purchase["picker"]["items"][number];

/**
 * A 1-tap purchase (LIS-1173): the purchase flow every sale in the app runs
 * (PurchaseSheet) — the same light sheet, item selection, contact step and
 * button as the engine's (EngineTicketsSheet) — then the purchase checkout
 * (VendorCheckout: native, or the shared web renderer on app-web) for the
 * signed-in fan, all drawn from the server's description of the purchase.
 * Nothing here names or branches on a seller.
 *
 * - Signed out on app-web, the address and its code are asked for in the
 *   sheet; natively, the app's sign-in sheet, as for the engine's.
 * - Each checkout opened is a new one for the fan signed in then: a new
 *   sign-in (not only a new account) drops it, and nothing typed or started
 *   for one sign-in is shown to another.
 * - Only the attempt's id is remembered, for the fan and the access, so
 *   reopening resumes the server's attempt instead of starting another.
 * - While something is moving that the fan may not walk away from (a start
 *   or a stop not yet answered, a running attempt), the sheet will not
 *   close and the checkout's back is not offered.
 */
export const VendorTicketsSheet = forwardRef<
  SheetAdapterRef,
  {
    accessId: string;
    /** The access read's purchase (public, or the signed-in fan's). */
    purchase: Purchase;
    /** The purchase as read again for the fan (sold out since): the
     *  access's button shows what it says, "Stay updated" once nothing is
     *  left. */
    onPurchase?: (purchase: Purchase) => void;
  }
>(function VendorTicketsSheet({ accessId, purchase, onPurchase }, ref) {
  return (
    // The fan's details are typed here: the checkout keeps itself above the
    // keyboard, as the login email sheet does, and scrolls its form.
    <TicketsSheet ref={ref} keyboard="content">
      {({ closeModal }) => (
        <VendorTicketsSheetContent accessId={accessId} purchase={purchase} close={closeModal} onPurchase={onPurchase} />
      )}
    </TicketsSheet>
  );
});

/** The purchase's items on sale, as the ticket screen draws them: the
 *  booking fee in each price; the order fee is the total's. */
export const vendorTiersOf = (purchase: Purchase, quantities: Record<string, number> = {}): EventTier[] =>
  purchase.picker.items
    .filter((item) => item.available)
    .map((item) => ({
      engineItemId: item.id,
      name: item.name,
      // A member's club discount code (LIS-1178): their price, the
      // listing's struck through beside it.
      priceMinor: (item.memberPriceMinor ?? item.priceMinor) + item.feeMinor,
      ...(item.memberPriceMinor !== undefined ? { listPriceMinor: item.priceMinor + item.feeMinor } : {}),
      currency: purchase.currency,
      minorUnits: "iso4217",
      active: true,
      soldOut: false,
      gated: false,
      saleStartsAt: null,
      saleEndsAt: null,
      // The item's maximum, or the room the order has left (the
      // purchase's most per order): its + stops there, as Link in Bio's
      // does.
      maxPerOrder: Math.min(item.max ?? Infinity, vendorRoom(purchase, quantities, item.id)),
    }));

/** An item's quantity within its limits: up from none to its minimum at
 *  once, down below it to none, never over its maximum or the `room` the
 *  order has left for it; an increase that does not fit stays put. */
export function vendorQuantity(item: Pick<Item, "min" | "max">, current: number, next: number, room = Infinity) {
  if (next <= 0) return 0;
  const most = Math.min(item.max ?? Infinity, room);
  if (next < item.min) return next > current ? (item.min <= most ? item.min : current) : 0;
  return next > current ? Math.max(current, Math.min(next, most)) : Math.min(next, item.max ?? Infinity);
}

/** How many units the order has left for an item: the purchase's most per
 *  order less the other items chosen. */
export const vendorRoom = (
  purchase: Pick<Purchase, "maxOrderQuantity">,
  quantities: Record<string, number>,
  itemId: string,
) =>
  purchase.maxOrderQuantity === null
    ? Infinity
    : purchase.maxOrderQuantity -
      Object.entries(quantities).reduce((sum, [id, quantity]) => sum + (id === itemId ? 0 : quantity), 0);

/** What is chosen that the purchase as read now still sells: nothing of an
 *  item gone or sold out since, and no more of one than its limit or the
 *  order's room. */
const heldQuantities = (purchase: Purchase, quantities: Record<string, number>) => {
  let room = purchase.maxOrderQuantity ?? Infinity;
  const held: Record<string, number> = {};
  for (const item of purchase.picker.items) {
    const quantity = Math.min(quantities[item.id] ?? 0, item.max ?? Infinity, room);
    if (!item.available || quantity <= 0 || quantity < item.min) continue;
    held[item.id] = quantity;
    room -= quantity;
  }
  return held;
};

/** Whether an open checkout's lines are still an order of the purchase as
 *  read now: each item on sale within its limits, the order within its
 *  most. */
export function linesHold(purchase: Purchase, lines: readonly { item_id: string; quantity: number }[]) {
  let units = 0;
  for (const line of lines) {
    const item = purchase.picker.items.find((candidate) => candidate.id === line.item_id);
    if (!item || !item.available || line.quantity < item.min || line.quantity > (item.max ?? Infinity)) return false;
    units += line.quantity;
  }
  return lines.length > 0 && (purchase.maxOrderQuantity === null || units <= purchase.maxOrderQuantity);
}

/** Where the order goes until the buyer's address says: the country the
 *  purchase was read for. */
const deliveryCountryOf = (purchase: Purchase) =>
  purchase.delivery.kind === "ladder" || purchase.delivery.kind === "quoted" ? purchase.delivery.country : null;

type Session = {
  id: string;
  user: AuthUser;
  /** What is bought, before any delivery the fan chooses. */
  lines: { item_id: string; quantity: number }[];
  /** A merch order's delivery as the store answered it for these lines
   *  (`ready`); null for tickets. */
  quote: Extract<PurchaseDeliveryQuote, { status: "ready" }> | null;
  transport: PurchaseTransport;
  /** An initial demo shows its notice. A checkout already open keeps its
   *  address form when another destination is a demo, so it can be changed. */
  openedAsDemo: boolean;
};

const nothing = () => undefined;

const IDLE: PurchaseDeliveryQuote = { status: "idle", key: null };
const idle = () => IDLE;
const noSubscription = () => () => {};

type VendorTicketsSheetContentProps = {
  accessId: string;
  purchase: Purchase;
  /** Closes the sheet. */
  close?: () => void;
  onPurchase?: (purchase: Purchase) => void;
};

export function VendorTicketsSheetContent(props: VendorTicketsSheetContentProps) {
  return <VendorAccessSheet key={props.accessId} {...props} />;
}

/** What the buyer typed in this sheet's checkout, kept across its
 *  remounts for that buyer alone (by their uid); nothing is saved before a
 *  start. */
type BuyerDrafts = {
  read: (uid: string) => FormDraft | undefined;
  keep: (uid: string, draft: FormDraft | null) => void;
};

function VendorAccessSheet({ accessId, purchase, close = nothing, onPurchase }: VendorTicketsSheetContentProps) {
  const { purchaseTransport, auth } = useRuntime().ports;
  const data = useData();
  const user = useAppBuyer().user;
  const [publicPurchase] = useState(purchase);
  const [fresh, setFresh] = useState<{
    user: AuthUser | null;
    purchase: Purchase;
    deliverTo: string | null;
  } | null>(null);
  // A buyer's read belongs to that sign-in, never the next buyer's.
  const current = fresh && fresh.user === user ? fresh.purchase : publicPurchase;
  // The country the buyer's address named in a checkout of this sheet: its
  // next checkout reads the purchase for it first.
  const named = fresh && fresh.user === user ? fresh.deliverTo : null;
  const deliverTo = named ?? deliveryCountryOf(current);
  const funnelOpening = useSheetFunnelOpening();
  // The Details' purchase funnel (LIS-1110): every start carries its ID,
  // or one of the sheet's own where the Details has none.
  const funnel = usePurchaseFunnel();
  const [ownFunnel] = useState(() => randomUUID());
  const funnelId = funnel?.id ?? ownFunnel;
  // The sheet's latest read, written only here, as it is accepted.
  const latest = useRef(fresh);
  const corrected = useCallback(
    (next: Purchase, read: { deliverTo: string | null }) => {
      const own = latest.current?.user === user ? latest.current : null;
      // A read for the fan's own country (a checkout's first, before its
      // address names one) never replaces the purchase this sheet read for
      // the country the buyer's address names.
      if (read.deliverTo === null && own?.deliverTo) return;
      const value = { user, purchase: next, deliverTo: read.deliverTo };
      latest.current = value;
      setFresh(value);
      // The access shows what the latest read says (sold out since).
      onPurchase?.(next);
    },
    [onPurchase, user],
  );
  const buyerDraft = useRef<{ uid: string; draft: FormDraft } | undefined>(undefined);
  const buyerDrafts = useMemo<BuyerDrafts>(
    () => ({
      read: (uid) => (buyerDraft.current?.uid === uid ? buyerDraft.current.draft : undefined),
      keep: (uid, draft) => {
        buyerDraft.current = draft ? { uid, draft } : undefined;
      },
    }),
    [],
  );
  // The sheet's calls, made as the fan is when each is made (signed out on
  // app-web, then signed in): one transport per sign-in.
  const read = useCallback<PurchaseTransport["read"]>((id, at) => readAccessPurchase(data, id, at), [data]);
  const signedIn = useMemo(
    () => (user ? purchaseTransport(user, auth.currentUser, read, funnelId) : null),
    [user, purchaseTransport, auth, read, funnelId],
  );
  const others = useRef<{ user: AuthUser | null; funnelId: string; transport: PurchaseTransport } | null>(null);
  const transportFor = useCallback(
    (fan: AuthUser | null) => {
      if (fan && fan === user && signedIn) return signedIn;
      if (!others.current || others.current.user !== fan || others.current.funnelId !== funnelId)
        others.current = { user: fan, funnelId, transport: purchaseTransport(fan, auth.currentUser, read, funnelId) };
      return others.current.transport;
    },
    [user, signedIn, purchaseTransport, auth, read, funnelId],
  );
  const [sheetId] = useState(() => randomUUID());
  // Once the fan signs in, the purchase is read again as theirs: a market
  // open to staff alone is a demo to everyone else.
  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    signedIn
      .read(accessId)
      .then((read) => {
        if (live && read) corrected(read, { deliverTo: null });
      })
      .catch(nothing);
    return () => {
      live = false;
    };
  }, [signedIn, accessId, corrected]);
  // Nothing is left to sell: the sheet has nothing to offer, and the
  // access says "Stay updated", as Link in Bio's page does.
  const soldOut = current.picker.items.every((item) => !item.available);
  useEffect(() => {
    if (soldOut) close();
  }, [soldOut, close]);
  if (soldOut) return null;
  return (
    <SheetContent
      key={JSON.stringify([current.listingId, current.picker.items.map((item) => item.id)])}
      accessId={accessId}
      purchase={current}
      sheetId={sheetId}
      deliverTo={deliverTo}
      named={named}
      signedIn={user && signedIn ? { user, transport: signedIn } : null}
      transportFor={transportFor}
      funnelOpening={funnelOpening}
      onPurchase={corrected}
      buyerDrafts={buyerDrafts}
      close={close}
    />
  );
}

function SheetContent({
  accessId,
  purchase,
  sheetId,
  deliverTo,
  named,
  signedIn,
  transportFor,
  funnelOpening,
  onPurchase,
  buyerDrafts,
  close,
}: {
  accessId: string;
  purchase: Purchase;
  sheetId: string;
  /** Where the order goes: the country the purchase was read for. */
  deliverTo: string | null;
  /** The country the buyer's address named in a checkout of this sheet. */
  named: string | null;
  /** The signed-in fan's transport. */
  signedIn: { user: AuthUser; transport: PurchaseTransport } | null;
  /** The sheet's transport for the fan as they are (one per sign-in). */
  transportFor: (fan: AuthUser | null) => PurchaseTransport;
  funnelOpening: SheetFunnelOpening;
  onPurchase: (purchase: Purchase, read: { deliverTo: string | null }) => void;
  buyerDrafts: BuyerDrafts;
  close: () => void;
}) {
  const runtime = useRuntime();
  const { auth } = runtime.ports;
  const buyer = useAppBuyer();
  const firebaseUser = buyer.user;
  const funnel = usePurchaseFunnel();
  const goods = purchase.goods;
  const [now] = useState(() => Date.now());

  const callsFor = useCallback(() => transportFor(firebaseUser ?? null), [firebaseUser, transportFor]);
  const latestCalls = useRef(callsFor);
  useEffect(() => {
    latestCalls.current = callsFor;
  }, [callsFor]);
  // A merch order's delivery, quoted in this sheet: the store's own
  // estimate of each basket, or the listing's ladder, as Link in Bio's
  // sheet quotes it.
  const [delivery, setDelivery] = useState<PurchaseDeliveryController | null>(null);
  useEffect(() => {
    if (goods !== "merch") return;
    const controller = createPurchaseDelivery({
      accessId,
      sheetId,
      transport: {
        estimateDelivery: (input) => latestCalls.current().estimateDelivery(input),
        deliveryEstimate: (id, estimate) => latestCalls.current().deliveryEstimate(id, estimate),
      },
    });
    setDelivery(controller);
    return () => controller.dispose();
  }, [goods, accessId, sheetId]);
  // A new read is followed at the destination the checkout last named (the
  // controller keeps it as it is named, pending or not), never back to the
  // country's stand-in under it.
  useEffect(() => {
    delivery?.follow(purchase, delivery.destination());
  }, [delivery, purchase]);
  // A purchase the fan started earlier, picked up again by the checkout:
  // Continue goes on to it whatever the store says of the basket now.
  const resumable = useRestoredAttempt(goods === "merch" && firebaseUser ? attemptKey(firebaseUser.uid, accessId) : null) != null;
  const quote = useSyncExternalStore(delivery?.subscribe ?? noSubscription, delivery?.snapshot ?? idle, delivery?.snapshot ?? idle);
  // A download its store emails is posted nowhere: no delivery to wait
  // for, say or quote, as Link in Bio's sheet has it.
  const posted = purchase.delivery.kind === "emailed" ? null : delivery;

  // A product chosen by its options: a line per combination, a dropdown
  // per option, as Link in Bio's sheet draws them.
  const options = useMemo(() => optionSetOfPicker(purchase.picker), [purchase]);
  const mostPerOrder = purchase.maxOrderQuantity;
  const [optionLines, setOptionLines] = useState<OptionLine[]>(() => (options ? openingLines(options, mostPerOrder) : []));
  const [listQuantities, setQuantities] = useState<Record<string, number>>(() => {
    if (options) return {};
    const first = purchase.picker.items.find((item) => item.available);
    return first ? { [first.id]: first.min } : {};
  });
  // The purchase read again may sell less: what is chosen keeps to it.
  const [heldFor, setHeldFor] = useState(purchase);
  if (heldFor !== purchase) {
    const previousOptions = optionSetOfPicker(heldFor.picker);
    setHeldFor(purchase);
    setQuantities((given) => heldQuantities(purchase, previousOptions ? lineQuantities(previousOptions, optionLines) : given));
    if (options)
      setOptionLines((given) => {
        const held = heldLines(options, given, mostPerOrder);
        return held === given ? given : held.length ? [...held] : openingLines(options, mostPerOrder);
      });
  }
  const quantities = useMemo(
    () => heldQuantities(purchase, options ? lineQuantities(options, optionLines) : listQuantities),
    [purchase, options, optionLines, listQuantities],
  );
  const tiers = useMemo(() => vendorTiersOf(purchase, quantities), [purchase, quantities]);
  const lines = purchase.picker.items
    .filter((item) => item.available && quantities[item.id])
    .map((item) => ({ item_id: item.id, quantity: quantities[item.id]! }));
  const selection = JSON.stringify(lines);
  // What is chosen, for the purchase funnel: the prices, before booking and
  // order fees and any delivery.
  const chosenForFunnel = purchaseSelection(
    lines.reduce((n, line) => n + line.quantity, 0),
    lines.reduce(
      (sum, line) => {
        const item = purchase.picker.items.find((candidate) => candidate.id === line.item_id);
        return sum + (item?.memberPriceMinor ?? item?.priceMinor ?? 0) * line.quantity;
      },
      0,
    ),
    purchase.currency,
  );
  // The store is asked about the basket as soon as it is known, and again
  // at every change.
  useEffect(() => {
    delivery?.request(JSON.parse(selection) as typeof lines);
  }, [delivery, selection]);
  // Continue's total: the order's, with the cheapest delivery the store
  // offers until the fan chooses; the items' while the store is asked. A
  // demo says what the order costs too, as its page does: its checkout
  // says why it can't be bought.
  const cheapest = quote.status === "ready" ? (quote.options[0] ?? null) : null;
  const total = orderTotal(
    { ...purchase, sale: { kind: "buyable" } },
    lines.map((line) => ({ id: line.item_id, quantity: line.quantity })),
    cheapest ? { feeMinor: cheapest.feeMinor } : null,
  );
  const shownMinor =
    total.kind === "complete" ? total.totalMinor : total.kind === "pending_delivery" ? total.subtotalMinor : null;
  // The store cannot post the basket as chosen, or did not answer.
  const deliveryBlocked =
    !resumable && (quote.status === "short" || quote.status === "undeliverable" || quote.status === "failed");
  const nameOf = (itemId: string) => purchase.picker.items.find((item) => item.id === itemId)?.name ?? "this";

  // The order no longer holds: the purchase, read again as the fan's own,
  // for the country their address named where it named one.
  const reread = useCallback(async () => {
    if (!signedIn) return;
    try {
      const again = await signedIn.transport.read(accessId, named ? { deliverTo: named } : undefined);
      if (again) onPurchase(again, { deliverTo: named });
    } catch {
      /* The server will still refuse an order that changed. */
    } finally {
      // The store's delivery no longer holds either: asked again.
      delivery?.invalidate();
    }
  }, [signedIn, accessId, named, onPurchase, delivery]);

  const flow: PurchaseFlow<Session> = {
    testID: "vendor-tickets-sheet",
    goods: goods === "merch" ? "items" : "tickets",
    keyboard: "content",
    buyer,
    onFunnelStep: funnel?.step,
    funnelOpening,
    choice: {
      ready: true,
      selection: chosenForFunnel,
      // Plain "Continue" for a free order, as the event's own sheet says
      // it; before the tax a seller adds at checkout.
      words:
        shownMinor !== null && shownMinor > 0
          ? withLocalTax(`Continue · ${money(shownMinor, purchase.currency)}`, purchase.tax === "added_at_checkout")
          : "Continue",
      disabled: !lines.length || total.kind === "invalid" || deliveryBlocked,
      note:
        posted && purchase.sale.kind === "buyable" ? (
          <DeliveryQuoteNote
            quote={quote}
            country={deliverTo}
            currency={purchase.currency}
            nameOf={nameOf}
            onRetry={() => posted.retry()}
          />
        ) : null,
      render: ({ locked }) => (
        <TicketSelection
          goods={goods}
          optionLabel={optionLabelOfPicker(purchase.picker) ?? undefined}
          options={options ? { set: options, lines: optionLines, mostPerOrder, onChange: setOptionLines } : undefined}
          loading={false}
          failed={false}
          onRetry={nothing}
          locked={locked}
          tickets={{
            tiers,
            now,
            quantities,
            lines,
            busy: false,
            message: undefined,
            waitlistNeedsEmail: false,
            onQuantity: (itemId, next) => {
              const item = purchase.picker.items.find((candidate) => candidate.id === itemId);
              if (!item) return;
              setQuantities((given) => {
                const held = heldQuantities(purchase, given);
                return {
                  ...held,
                  [itemId]: vendorQuantity(item, held[itemId] ?? 0, next, vendorRoom(purchase, held, itemId)),
                };
              });
            },
            onWaitlist: nothing,
          }}
        />
      ),
    },
    checkout: {
      // The checkout, for one sign-in and one selection. A merch order waits
      // for the store's delivery first ("Getting delivery…"), and goes back
      // to what is bought, which says why, when the store cannot post it.
      open: async () => {
        const user = firebaseUser ?? auth.currentUser();
        if (!lines.length || !user) return { kind: "back" };
        const answered = posted && purchase.sale.kind === "buyable" ? await settledQuote(posted) : null;
        if (answered && (answered.status !== "ready" || !answered.options.length) && !resumable) return { kind: "back" };
        const base = transportFor(user);
        return {
          kind: "checkout",
          checkout: {
            id: randomUUID(),
            user,
            openedAsDemo: purchase.sale.kind === "demo",
            lines: JSON.parse(selection) as typeof lines,
            quote: answered?.status === "ready" ? (answered as Extract<PurchaseDeliveryQuote, { status: "ready" }>) : null,
            transport: base,
          },
        };
      },
      openingWords: () => (posted && quote.status === "loading" ? "Getting delivery…" : null),
      key: (session) => session.id,
      owner: (session) => session.user,
      shape: (session) =>
        session && linesHold(purchase, session.lines) ? (
          <CheckoutWaiting
            purchase={purchase}
            lines={session.lines}
            delivery={session.quote?.options[0] ? { option: session.quote.options[0], named: session.quote.options.length > 1 } : null}
          />
        ) : null,
      // The purchase read again may no longer sell what was chosen: said,
      // for the fan to go back and choose again, as Link in Bio says it.
      render: (session, host) =>
        !linesHold(purchase, session.lines) ? (
          <OrderChanged host={host} goods={goods} />
        ) : purchase.sale.kind === "demo" && session.openedAsDemo && !demoChangeable(purchase) ? (
          <DemoCheckout purchase={purchase} lines={session.lines} message={purchase.sale.message} host={host} />
        ) : (
          <PurchaseSessionCheckout
            session={session}
            accessId={accessId}
            purchase={purchase}
            sheetId={sheetId}
            named={named}
            delivery={delivery}
            quote={quote.status === "ready" ? quote : null}
            host={host}
            onReselect={reread}
            onPurchase={(read, at) => {
              onPurchase(read, at);
            }}
            buyerDrafts={buyerDrafts}
          />
        ),
    },
  };
  return <PurchaseSheet flow={flow} close={close} />;
}

/**
 * One checkout, for one sign-in: a merch order's Delivery step first when
 * the store offers more than one option, then the checkout. The attempt
 * remembered for this fan and access is read first (the checkout loads
 * only once it is known), and the attempt the checkout adopts is
 * remembered under the same key.
 */
function PurchaseSessionCheckout({
  session,
  accessId,
  purchase,
  sheetId,
  named,
  delivery,
  quote,
  host,
  onReselect,
  onPurchase,
  buyerDrafts,
}: {
  session: Session;
  accessId: string;
  purchase: Purchase;
  sheetId: string;
  named: string | null;
  delivery: PurchaseDeliveryController | null;
  quote: Extract<PurchaseDeliveryQuote, { status: "ready" }> | null;
  host: CheckoutHost;
  onReselect: () => Promise<void>;
  onPurchase: (purchase: Purchase, read: { deliverTo: string | null }) => void;
  buyerDrafts: BuyerDrafts;
}) {
  const { clock, platform } = useRuntime().ports;
  const currentQuote = quote ?? session.quote;
  const options = currentQuote?.options ?? [];
  // Keep the chosen service when a new destination is quoted. Otherwise
  // start with the cheapest.
  const [chosenCode, setChosenCode] = useState(options[0]?.code);
  const chosen = options.find((option) => option.code === chosenCode) ?? options[0] ?? null;
  const [choosing, setChoosing] = useState(options.length > 1);
  const key = attemptKey(session.user.uid, accessId);
  const restoreId = useRestoredAttempt(key);
  const remember = useAttemptWriter(key);
  const { back: toTickets, chrome } = host;
  // The order changed: back to the tickets, read again.
  const reselect = useCallback(async () => {
    toTickets();
    await onReselect();
  }, [toTickets, onReselect]);
  const total = orderTotal(
    purchase,
    session.lines.map((line) => ({ id: line.item_id, quantity: line.quantity })),
    chosen ? { feeMinor: chosen.feeMinor } : null,
  );
  // The Delivery step's button: Continue with the option chosen.
  const totalMinor = total.kind === "complete" ? total.totalMinor : null;
  useEffect(() => {
    if (!choosing) return;
    chrome({
      button: {
        words:
          totalMinor === null
            ? "Continue"
            : withLocalTax(`Continue · ${money(totalMinor, purchase.currency)}`, purchase.tax === "added_at_checkout"),
        icon: null,
        plain: false,
        done: false,
        disabled: !chosen,
        onPress: () => setChoosing(false),
      },
      hold: false,
      stayPut: false,
    });
  }, [choosing, chosen, chrome, totalMinor, purchase.currency, purchase.tax]);
  // The Delivery step is a screen of its own in the funnel.
  const delivering = choosing && !!chosen;
  const { delivering: onDelivering } = host;
  useEffect(() => {
    onDelivering(delivering);
    return () => onDelivering(false);
  }, [delivering, onDelivering]);
  if (choosing && chosen) {
    const items = purchaseOrderOf(purchase, session.lines, null, null);
    const goodsMinor = session.lines.reduce((sum, line) => {
      const item = purchase.picker.items.find((candidate) => candidate.id === line.item_id);
      return item ? sum + line.quantity * ((item.memberPriceMinor ?? item.priceMinor) + item.feeMinor) : sum;
    }, 0);
    return (
      <DeliveryChoiceView
        order={items}
        options={options}
        chosen={chosen}
        goodsMinor={goodsMinor}
        currency={purchase.currency}
        onChoose={(option) => setChosenCode(option.code)}
        onBack={toTickets}
      />
    );
  }
  const orderDelivery: OrderDelivery | null = chosen ? { option: chosen, named: options.length > 1 } : null;
  if (restoreId === undefined)
    return <CheckoutWaiting purchase={purchase} lines={session.lines} delivery={orderDelivery} onBack={toTickets} />;
  return (
    <VendorCheckout
      clock={clock}
      transport={session.transport}
      accessId={accessId}
      purchase={purchase}
      lines={session.lines}
      delivery={delivery}
      deliveryCode={chosen?.code ?? null}
      sheetId={sheetId}
      deliverTo={named}
      restoreId={restoreId}
      onAttempt={remember}
      newId={randomUUID}
      onBack={options.length > 1 ? () => setChoosing(true) : toTickets}
      onReselect={reselect}
      onChrome={host.chrome}
      onHolding={host.holding}
      onSucceeded={host.succeeded}
      buyerCountry={platform.countryCode()}
      onPurchase={(read, at) => {
        if (read) onPurchase(read, at);
      }}
      initialDraft={buyerDrafts.read(session.user.uid)}
      onDraftChange={(draft) => buyerDrafts.keep(session.user.uid, draft)}
      // The fan got this far and could not go on, as Link in Bio records
      // it. The app knows the access, not the artist's page.
      onBlocked={(reason) =>
        track("checkout_blocked", {
          artist_slug: null,
          access_id: accessId,
          reason,
          currency: purchase.currency,
          visitor_country: null,
        })
      }
    />
  );
}

/** The checkout's own shape while its parts are read: the header, the
 *  order and the rows to come; the sheet's button says "Loading
 *  checkout". */
function CheckoutWaiting({
  purchase,
  lines,
  delivery = null,
  onBack,
}: {
  purchase: Purchase;
  lines: { item_id: string; quantity: number }[];
  /** A merch order's delivery chosen: said in the order's summary. */
  delivery?: OrderDelivery | null;
  onBack?: () => void;
}) {
  const words = goodsWords(purchase.goods, purchase.delivery.kind === "emailed");
  const total = orderTotal(
    purchase,
    lines.map((line) => ({ id: line.item_id, quantity: line.quantity })),
    delivery ? { feeMinor: delivery.option.feeMinor } : null,
  );
  return (
    <View testID="vendor-checkout-waiting">
      <View style={styles.header}>
        <LPressable
          accessibilityLabel={words.back}
          accessibilityRole="button"
          accessibilityState={{ disabled: !onBack }}
          disabled={!onBack}
          hitSlop={10}
          onPress={onBack}
          sentryEventName="VendorTicketsSheet.Back"
          style={styles.back}
        >
          <Chevron />
        </LPressable>
      </View>
      <View style={styles.summary}>
        <OrderSummaryView
          {...purchaseOrderOf(purchase, lines, delivery, total.kind === "complete" ? total.totalMinor : null)}
        />
      </View>
      <VendorShape destination={words.destination} />
    </View>
  );
}

/** In place of a checkout whose order the purchase as read now no longer
 *  holds: what changed is said, with the way back to choose again. */
function OrderChanged({ host, goods }: { host: CheckoutHost; goods: Purchase["goods"] }) {
  const { chrome } = host;
  // Nothing to pay: no button under it.
  useEffect(() => chrome({ button: null, hold: false }), [chrome]);
  return (
    <View testID="vendor-order-changed">
      <View style={styles.header}>
        <LPressable
          accessibilityLabel={goodsWords(goods).back}
          accessibilityRole="button"
          hitSlop={10}
          onPress={host.back}
          sentryEventName="VendorTicketsSheet.OrderChangedBack"
          style={styles.back}
        >
          <Chevron />
        </LPressable>
      </View>
      <Text accessibilityRole="text" style={styles.demo}>
        {ORDER_CHANGED}
      </Text>
    </View>
  );
}

/** A demo's checkout that its form can't change (no address reprices it):
 *  the order chosen, and why it can't be bought here, in the server's
 *  words. No card is asked for, no estimate and no purchase starts. */
function DemoCheckout({
  purchase,
  lines,
  message,
  host,
}: {
  purchase: Purchase;
  lines: { item_id: string; quantity: number }[];
  message: string;
  host: CheckoutHost;
}) {
  const words = goodsWords(purchase.goods, purchase.delivery.kind === "emailed");
  const { chrome } = host;
  // Nothing to pay: no button under it.
  useEffect(() => chrome({ button: null, hold: false }), [chrome]);
  return (
    <View testID="vendor-checkout-demo">
      <View style={styles.header}>
        <LPressable
          accessibilityLabel={words.back}
          accessibilityRole="button"
          hitSlop={10}
          onPress={host.back}
          sentryEventName="VendorTicketsSheet.DemoBack"
          style={styles.back}
        >
          <Chevron />
        </LPressable>
      </View>
      <View style={styles.summary}>
        <OrderSummaryView {...purchaseOrderOf(purchase, lines, null, null)} />
      </View>
      <Text testID="demo-listing-notice" accessibilityRole="text" style={styles.demo}>
        {message}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 12,
    minHeight: 28,
  },
  back: {
    alignItems: "center",
    height: 28,
    justifyContent: "center",
    marginLeft: -6,
    width: 28,
  },
  summary: { marginBottom: 12 },
  demo: {
    backgroundColor: colors.fill,
    borderRadius: 14,
    color: colors.text,
    fontSize: 15,
    fontWeight: "600",
    overflow: "hidden",
    paddingHorizontal: 16,
    paddingVertical: 14,
    textAlign: "center",
  },
});
